import { useState, useEffect } from 'react'
import { FiTrendingUp, FiCalendar, FiCheck, FiX, FiLoader } from 'react-icons/fi'
import { horasExtrasAPI } from '../../services/api'
import { PageHeader, Stat, Card, Empty, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * MisHorasExtras (V2) — versión rediseñada con DS v2.
 * Misma lógica que el original. Cambian:
 *   - PageHeader v2 con eyebrow numerada
 *   - 3 Stat KPIs en lugar de cards con gradient
 *   - Card + Pill primitives para la lista de solicitudes
 */

const fmtMin = (m) => {
  if (!m) return '0m'
  const h = Math.floor(m / 60), min = m % 60
  return h > 0 && min > 0 ? `${h}h ${min}m` : h > 0 ? `${h}h` : `${min}m`
}

const ESTADO_TONE = {
  pendiente: { tone: 'amber',   icon: FiLoader, label: 'Pendiente' },
  aprobada:  { tone: 'emerald', icon: FiCheck,  label: 'Aprobada' },
  rechazada: { tone: 'red',     icon: FiX,      label: 'Rechazada' },
}

const MisHorasExtrasV2 = () => {
  const [solicitudes, setSolicitudes] = useState([])
  const [loading, setLoading] = useState(true)
  const [anio, setAnio] = useState(new Date().getFullYear())
  const anios = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i)

  useEffect(() => { cargar() }, [anio]) // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = async () => {
    setLoading(true)
    try {
      const res = await horasExtrasAPI.misSolicitudes({ anio })
      setSolicitudes(res.data || [])
    } catch { /* silencioso */ }
    finally { setLoading(false) }
  }

  const pendientes = solicitudes.filter(s => s.estado === 'pendiente').length
  const aprobadas = solicitudes.filter(s => s.estado === 'aprobada')
  const rechazadas = solicitudes.filter(s => s.estado === 'rechazada').length
  const totalAprobado = aprobadas.reduce((acc, s) => acc + (s.minutos_aprobados || s.minutos_extra), 0)

  return (
    <>
      <PageHeader
        num="§ HX"
        kicker="Mi historial"
        title="Mis horas extras."
        italic="Solicitudes y estado."
        sub={`Año ${anio} · ${solicitudes.length} ${solicitudes.length === 1 ? 'solicitud' : 'solicitudes'}`}
        actions={
          <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
            <FiCalendar size={13} className="text-[var(--ink-3)]" />
            <label className="text-[var(--ink-3)] whitespace-nowrap">Año</label>
            <select
              value={anio}
              onChange={e => setAnio(parseInt(e.target.value))}
              className="bg-transparent outline-none text-[var(--ink)] font-medium cursor-pointer"
            >
              {anios.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        }
      />

      <div className="px-7 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat
            label="Pendientes"
            value={pendientes}
            sub={pendientes > 0 ? 'Esperando aprobación' : 'Nada en cola'}
          />
          <Stat
            label="Aprobadas"
            value={aprobadas.length}
            sub={totalAprobado > 0 ? `${fmtMin(totalAprobado)} totales` : 'Sin horas aprobadas'}
            delta={totalAprobado > 0 ? fmtMin(totalAprobado) : null}
            deltaTone="emerald"
          />
          <Stat
            label="Rechazadas"
            value={rechazadas}
            sub={rechazadas > 0 ? 'Revisa el motivo' : 'Ninguna'}
          />
        </div>

        {/* Lista */}
        {loading ? (
          <Card eyebrow="Detalle" title="Cargando…">
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 rounded-soft bg-[var(--bg-2)] animate-pulse" />
              ))}
            </div>
          </Card>
        ) : solicitudes.length === 0 ? (
          <Card>
            <Empty
              title="Sin solicitudes."
              sub={`No tienes solicitudes de horas extra registradas en ${anio}.`}
            />
          </Card>
        ) : (
          <Card
            eyebrow="Detalle"
            title="Solicitudes registradas"
            action={<span className="text-[11.5px] text-[var(--ink-3)] tnum">{solicitudes.length} {solicitudes.length === 1 ? 'registro' : 'registros'}</span>}
            bodyClassName="p-0"
          >
            <div className="divide-y divide-[var(--line)]">
              {solicitudes.map(s => {
                const est = ESTADO_TONE[s.estado] || ESTADO_TONE.pendiente
                const Icon = est.icon
                const esParcial = s.estado === 'aprobada' && s.minutos_aprobados && s.minutos_aprobados < s.minutos_extra
                return (
                  <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 text-[12.5px]">
                    {/* Fecha */}
                    <div className="flex items-center gap-2 text-[var(--ink-3)] sm:w-36 shrink-0">
                      <FiCalendar size={13} />
                      <span className="font-mono tnum">
                        {formatearFecha(s.fecha)}
                      </span>
                    </div>

                    {/* Tiempo */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FiTrendingUp size={13} className="text-[var(--brand-1)] shrink-0" />
                      {esParcial ? (
                        <span>
                          <span className="font-mono tnum font-medium text-[#0F8C66]">{fmtMin(s.minutos_aprobados)}</span>
                          <span className="text-[var(--ink-3)]"> de {fmtMin(s.minutos_extra)} solicitados</span>
                        </span>
                      ) : (
                        <span className="font-mono tnum font-medium text-[var(--brand-1)]">+{fmtMin(s.minutos_extra)}</span>
                      )}
                      {s.motivo && (
                        <span className="text-[11.5px] text-[var(--ink-3)] italic truncate ml-2 hidden sm:inline">"{s.motivo}"</span>
                      )}
                    </div>

                    {/* Estado */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Pill tone={est.tone}>
                        <Icon size={11} /> {est.label}
                        {esParcial && <span className="text-[10px] opacity-75">(parcial)</span>}
                      </Pill>
                    </div>

                    {/* Comentario admin */}
                    {s.comentario_admin && (
                      <span className="text-[11px] text-[var(--ink-3)] italic max-w-[200px] truncate shrink-0 hidden md:block" title={s.comentario_admin}>
                        {s.comentario_admin}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        )}
      </div>
    </>
  )
}

export default MisHorasExtrasV2
